import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

const useDataTable = ({
  endpoint,
  initialSort = { key: 'created_at', order: 'desc' },
  pageSize = 50,
  initialFilters = {},
  autoFetch = true,
  enablePinning = false,
  persistenceKey = null,
  enableUrlPersistence = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [pinnedItems, setPinnedItems] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: pageSize,
    total: 0,
    totalPages: 0,
  });

  const [sorting, setSorting] = useState(initialSort);
  const [filtering, setFiltering] = useState(initialFilters);

  const [columnVisibility, setColumnVisibility] = useState({});

  const serializeFiltersToUrl = useCallback((filters) => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        value !== false
      ) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, JSON.stringify(value));
          }
        } else {
          params.set(key, value);
        }
      }
    });

    return params;
  }, []);

  const deserializeFiltersFromUrl = useCallback(() => {
    if (!enableUrlPersistence) return initialFilters;

    const urlFilters = {};

    searchParams.forEach((value, key) => {
      try {
        if (value.startsWith('[') || value.startsWith('{')) {
          urlFilters[key] = JSON.parse(value);
        } else if (value === 'true') {
          urlFilters[key] = true;
        } else if (value === 'false') {
          urlFilters[key] = false;
        } else {
          urlFilters[key] = value;
        }
      } catch (error) {
        console.error(error);
        urlFilters[key] = value;
      }
    });

    return { ...initialFilters, ...urlFilters };
  }, [searchParams, enableUrlPersistence, initialFilters]);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (enableUrlPersistence && !isInitialized) {
      const urlFilters = deserializeFiltersFromUrl();
      setFiltering(urlFilters);
      setIsInitialized(true);
    }
  }, [enableUrlPersistence, deserializeFiltersFromUrl, isInitialized]);

  useEffect(() => {
    if (enableUrlPersistence && isInitialized) {
      const params = serializeFiltersToUrl(filtering);
      setSearchParams(params, { replace: true });
    }
  }, [
    filtering,
    enableUrlPersistence,
    serializeFiltersToUrl,
    setSearchParams,
    isInitialized,
  ]);

  useEffect(() => {
    if (enablePinning && persistenceKey) {
      const stored = localStorage.getItem(`pinned_${persistenceKey}`);
      if (stored) {
        try {
          setPinnedItems(JSON.parse(stored));
        } catch {
          setPinnedItems([]);
        }
      }
    }
  }, [enablePinning, persistenceKey]);

  useEffect(() => {
    if (enablePinning && persistenceKey) {
      localStorage.setItem(
        `pinned_${persistenceKey}`,
        JSON.stringify(pinnedItems),
      );
    }
  }, [pinnedItems, enablePinning, persistenceKey]);

  const buildQueryParams = useMemo(() => {
    const params = {
      page: pagination.page,
      limit: pagination.limit,
      sort_by: sorting.key,
      sort_order: sorting.order,
      ...filtering,
    };

    return Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== null && value !== undefined && value !== '',
      ),
    );
  }, [pagination.page, pagination.limit, sorting, filtering]);

  const fetchData = useCallback(async () => {
    if (!endpoint) return;

    setLoading(true);
    setError(null);

    try {
      const params = buildQueryParams;
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        params,
      });

      const { data: tableData, pagination: responsePagination } = response.data;

      if (pagination.page === 1) {
        setData(tableData || []);
      } else {
        setData((prev) => [...prev, ...(tableData || [])]);
      }

      setPagination((prev) => ({
        ...prev,
        total: responsePagination?.total || 0,
        totalPages: responsePagination?.totalPages || 0,
      }));

      setHasMore(pagination.page < (responsePagination?.totalPages || 0));
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || 'Failed to fetch data',
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [endpoint, buildQueryParams]);

  const handleSort = useCallback((key) => {
    setSorting((prev) => ({
      key,
      order: prev.key === key && prev.order === 'asc' ? 'desc' : 'asc',
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
    setData([]);
    setHasMore(true);
  }, []);

  const handleFilter = useCallback(
    (filterKey, filterValue) => {
      setFiltering((prev) => ({
        ...prev,
        [filterKey]: filterValue,
      }));
      setPagination((prev) => ({ ...prev, page: 1 }));
      setData([]);
      setHasMore(true);
    },
    [setFiltering, setPagination],
  );

  const debouncedLoadMore = useCallback(
    debounce(() => {
      setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
    }, 300),
    [],
  );

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    debouncedLoadMore();
  }, [loading, hasMore, debouncedLoadMore]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPagination((prev) => ({
      ...prev,
      limit: newPageSize,
      page: 1,
    }));
  }, []);

  const toggleColumn = useCallback((columnKey) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltering(initialFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setHasMore(true);
    if (enableUrlPersistence) {
      setSearchParams({}, { replace: true });
    }
  }, [initialFilters, enableUrlPersistence, setSearchParams]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (hasMore) {
      if (enableUrlPersistence && !isInitialized) {
        return;
      }
      fetchData();
    }
  }, [
    autoFetch,
    enableUrlPersistence,
    isInitialized,
    pagination.page,
    pagination.limit,
    sorting,
    filtering,
    fetchData,
  ]);

  const isColumnVisible = useCallback(
    (columnKey) => {
      return columnVisibility[columnKey] !== false;
    },
    [columnVisibility],
  );

  const setColumnVisibilityForAll = useCallback((columns) => {
    const initialVisibility = {};
    columns.forEach((column) => {
      if (typeof column === 'string') {
        initialVisibility[column] = true;
      } else if (column.key) {
        initialVisibility[column.key] = true;
      }
    });
    setColumnVisibility((prev) => ({ ...initialVisibility, ...prev }));
  }, []);

  const togglePin = useCallback(
    (item) => {
      if (!enablePinning) return;

      setPinnedItems((prev) => {
        const isAlreadyPinned = prev.some((p) => p.id === item.id);
        if (isAlreadyPinned) {
          return prev.filter((p) => p.id !== item.id);
        } else {
          return [...prev, item];
        }
      });
    },
    [enablePinning],
  );

  const isPinned = useCallback(
    (itemId) => {
      return pinnedItems.some((p) => p.id === itemId);
    },
    [pinnedItems],
  );

  const unpinItem = useCallback((itemId) => {
    setPinnedItems((prev) => prev.filter((p) => p.id !== itemId));
  }, []);

  const clearAllPinned = useCallback(() => {
    setPinnedItems([]);
  }, []);

  useEffect(() => {
    if (persistenceKey) {
      const stored = localStorage.getItem(`columns_${persistenceKey}`);
      if (stored) {
        try {
          const storedVisibility = JSON.parse(stored);
          setColumnVisibility(storedVisibility);
        } catch {
          setColumnVisibility({});
        }
      }
    }
  }, [persistenceKey]);

  useEffect(() => {
    if (persistenceKey && Object.keys(columnVisibility).length > 0) {
      localStorage.setItem(
        `columns_${persistenceKey}`,
        JSON.stringify(columnVisibility),
      );
    }
  }, [columnVisibility, persistenceKey]);

  const tableState = useMemo(
    () => ({
      data,
      loading,
      error,
      pagination,
      sorting,
      filtering,
      columnVisibility,
      hasMore,
      pinnedItems,
    }),
    [
      data,
      loading,
      error,
      pagination,
      sorting,
      filtering,
      columnVisibility,
      hasMore,
      pinnedItems,
    ],
  );

  return {
    ...tableState,
    handleSort,
    handleFilter,
    handlePageSizeChange,
    toggleColumn,
    resetFilters,
    refresh,
    isColumnVisible,
    setColumnVisibilityForAll,
    loadMore,
    togglePin,
    isPinned,
    unpinItem,
    clearAllPinned,
  };
};

export default useDataTable;
